import { Form, Formik } from "formik";
import { useState } from "react";
import { toast } from "react-toastify";
import settings, { EmployerType } from "../../../../apiConfigs/settings";
import Button from "../../../../designComponents/Button";
import Input from "../../../../designComponents/Input";
import Modal from "../../../../designComponents/Modal";
import { ModalContainerStyled } from "../../../../designComponents/Modal/style";
import Typography from "../../../../designComponents/Typography";
import UploadFile from "../../../../designComponents/UploadFile";
import theme from "../../../../theme";
import requestAPI from "../../../../utils/requestAPI";
import { uploadFilesToS3OnlyKeys } from "../../../../utils/s3Upload";
import { InfoRowStyled, NewLandlordWrapper } from "../../../Users/style";

type Props = {
  isOpenModal: boolean;
  onClose: () => void;
  refreshData: Function;
  initialValues: EmployerType;
};

const AddEmployer = ({
  isOpenModal,
  onClose,
  refreshData,
  initialValues,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const employerId = initialValues?._id;

  const handleAddEmployer = async (values: EmployerType) => {
    setLoading(true);
    let employerUrl = "";

    try {
      if (values.avatar && typeof values.avatar !== "string") {
        const uploadFiles = await uploadFilesToS3OnlyKeys({
          fileUri: [values.avatar],
          category: "avatar",
          userId: employerId ?? "",
        });
        if (uploadFiles && uploadFiles.length > 0) {
          employerUrl = uploadFiles[0];
        }
      } else if (typeof values.avatar === "string") {
        employerUrl = values.avatar;
      }

      await requestAPI(
        settings.addEmployer({
          name: values.name,
          avatar: employerUrl || "",
          country: values.country,
          address: values.address,
        })
      );
      setLoading(false);
      onClose();
      refreshData();
      toast.success("Employer added successfully");
    } catch (error: any) {
      console.log(error);
      setErrorMessage(error.message);
      setLoading(false);
    }
  };

  const handleUpdateEmployer = async (
    employerId: string,
    values: EmployerType
  ) => {
    setLoading(true);
    let employerUrl = "";

    try {
      if (values.avatar && typeof values.avatar !== "string") {
        const uploadFiles = await uploadFilesToS3OnlyKeys({
          fileUri: [values.avatar],
          category: "avatar",
          userId: employerId ?? "",
        });
        if (uploadFiles && uploadFiles.length > 0) {
          employerUrl = uploadFiles[0];
        }
      } else if (typeof values.avatar === "string") {
        employerUrl = values.avatar;
      }

      await requestAPI(
        settings.updateEmployer(employerId, {
          name: values.name,
          avatar: employerUrl || "",
          country: values.country,
          address: values.address,
        })
      );
      setLoading(false);
      onClose();
      refreshData();
      toast.success("Employer updated successfully");
    } catch (error: any) {
      console.log(error);
      setErrorMessage(error.message);
      setLoading(false);
    }
  };

  return (
    <ModalContainerStyled>
      <Modal
        isOpen={isOpenModal}
        onClose={onClose}
        title={employerId ? "Update Employer" : "Create New Employer"}
        halfWidth={true}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            if (employerId) {
              handleUpdateEmployer(employerId, values);
            } else {
              handleAddEmployer(values);
            }
          }}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, handleSubmit, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <NewLandlordWrapper>
                <InfoRowStyled>
                  <Typography
                    fontFamily="roboto"
                    size="normal"
                    fontWeight="medium"
                    color="dark.neutral_45"
                  >
                    Icon <span style={{ color: "red" }}>*</span>
                  </Typography>

                  <UploadFile
                    title="Upload Icon"
                    name="avatar"
                    variant="previewImage"
                    onChange={(file) => setFieldValue("avatar", file)}
                    apiUrls={
                      values.avatar && typeof values.avatar === "string"
                        ? [values.avatar]
                        : []
                    }
                    onRemove={() => {
                      setFieldValue("avatar", null);
                    }}
                    enableMultipleFile={false}
                  />
                </InfoRowStyled>

                <InfoRowStyled>
                  <Typography
                    fontFamily="roboto"
                    size="normal"
                    fontWeight="medium"
                    color="dark.neutral_45"
                  >
                    Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Input
                    name="name"
                    value={values.name}
                    onChange={(e) => setFieldValue("name", e.target.value)}
                  />
                  {errors.name && touched.name && (
                    <div style={{ color: "red" }}>{errors.name}</div>
                  )}
                </InfoRowStyled>

                <InfoRowStyled>
                  <Typography
                    fontFamily="roboto"
                    size="normal"
                    fontWeight="medium"
                    color="dark.neutral_45"
                  >
                    Country <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Input
                    name="country"
                    value={values.country}
                    onChange={(e) => setFieldValue("country", e.target.value)}
                  />
                  {errors.country && touched.country && (
                    <div style={{ color: "red" }}>{errors.country}</div>
                  )}
                </InfoRowStyled>

                <InfoRowStyled>
                  <Typography
                    fontFamily="roboto"
                    size="normal"
                    fontWeight="medium"
                    color="dark.neutral_45"
                  >
                    Address <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Input
                    name="address"
                    value={values.address}
                    onChange={(e) => setFieldValue("address", e.target.value)}
                  />
                  {errors.address && touched.address && (
                    <div style={{ color: "red" }}>{errors.address}</div>
                  )}
                </InfoRowStyled>
              </NewLandlordWrapper>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "50px",
                }}
              >
                <Button
                  radius="md"
                  variant="ghost"
                  style={{
                    border: `1px solid ${theme.colors.dark.neutral_250}`,
                    borderRadius: "10px",
                    color: theme.colors.text,
                    fontFamily: theme.fontFamily.roboto,
                    fontWeight: theme.fontWeights.medium,
                  }}
                  onClick={() => onClose()}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  radius="normal"
                  isLoading={loading}
                  style={{ backgroundColor: theme.colors.brandColorBlue }}
                >
                  {employerId ? "Update" : "Save"}
                </Button>
              </div>
              {errorMessage && (
                <div style={{ color: "red", marginTop: "10px" }}>
                  {errorMessage}
                </div>
              )}
            </Form>
          )}
        </Formik>
      </Modal>
    </ModalContainerStyled>
  );
};

export default AddEmployer;
